'use client';

import type { FC } from 'react';
import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSearchParams } from 'next/navigation';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { InspiringSearchPreviewFragmentFragmentDoc } from '@packages/gql/generated/shopping/InspiringSearchPreviewFragmentFragmentDoc';
import {
  Box,
  Typography,
  ShimmeringBlurOverlay,
  useRouterTransition,
  ErrorBoundary,
  usePushQueryChange,
  Button,
  searchParamsToObject,
} from '@packages/shared';

import { useAbTesting } from '@packages/abtesting';
import {
  INSPIRING_SEARCH_TEST_ID,
  INSPIRING_SEARCH_V2,
} from '@packages/modules/src/Header/useInspiringSearchSettings.shared';
import { useInspiringSearchSettings } from '@packages/modules/src/Header/useInspiringSearchSettings';
import { useOriginalSearchQuery } from '../../hooks/useOriginalSearchQuery';
import { ColorSelectFailedNotification } from '../ColorSelectFailedNotification';
import { SearchKimRating } from '../SearchKimRating';
import { InspiringSearchSuggestion } from '../InspiringSearchSuggestion/InspiringSearchSuggestion';
import { decodeInspiringSearchData, encodeInspiringSearchData } from '../../queryEncoding';

/* GraphQL */ `
  fragment InspiringSearchPreviewFragment on SuggestionPreviewSearchResult {
    suggestions {
      id
      items {
        styleId
        ...SearchProductCardFragment
      }
      query
      activeFilters {
        filterId
        activeValues {
          id
        }
      }
      ...InspiringSearchSuggestionFragment
    }
    ...SearchKimRatingFragment
  }
`;

/* GraphQL */ `
  query InspiringSearchPreviewLoadMoreData(
    $params: SearchParametersInput!
    $query: String!
    $searchQuery: String!
    $locale: String!
  ) {
    search(params: $params, query: $query) {
      __typename
      ...SearchResultPageDefaultContentFragment
      ...SearchResultPageZeroResultsFragment
      ...UseAbTestingCookiesFromEpsFragment
      ...InspiringSearchPreviewFragment
      ...SearchPageTrackingFragment
      ...SearchHandleSpecialResponsesFragment
      ...SearchHandleSpecialResponsesSearchPageFragment

      ... on SearchResultBase {
        id
      }
    }

    seoForSearch(searchQuery: $searchQuery, locale: $locale) {
      ...ProductListSeoFragment
      title
      description
      keywords
      robots
      canonicalUrl
    }
  }
`;

export type InspiredSearchPreviewProps = {
  maskedData: FragmentType<typeof InspiringSearchPreviewFragmentFragmentDoc>;
  referrer?: string;
};

const messages = defineMessages({
  pageTitle: {
    id: 'search.inspiringSearch.previewPage.pageTitle',
    defaultMessage: 'Inspirierende Suchergebnisse',
    description: 'page title for the inspiring search preview page',
  },
  header: {
    id: 'search.inspiringSearch.previewPage.header',
    defaultMessage: 'Suchvorschläge für',
    description: 'Inspired search preview page header',
  },
  loadMore: {
    id: 'search.inspiringSearch.previewPage.loadMore',
    defaultMessage: 'Mehr Vorschläge anzeigen',
    description: 'Button to display/load more suggested queries to the user',
  },
});

const LOAD_MORE_COUNT = 5;

/**
 * Preview page for AI-suggested search queries, listing multiple options to continue the user journey after the initial natural language query.
 * */
export const InspiringSearchPreview: FC<InspiredSearchPreviewProps> = ({
  maskedData,
  referrer,
}) => {
  const { setOutcome } = useAbTesting();
  const data = getFragmentData(InspiringSearchPreviewFragmentFragmentDoc, maskedData);

  const searchParams = useSearchParams();
  const persistedData = decodeInspiringSearchData(searchParamsToObject(searchParams));

  const originalSuggestions = data.suggestions.filter((x) => x.items.length);
  const availableAdditionalSuggestions =
    persistedData?.available ?? data.suggestions.filter((x) => !x.items.length);

  const { formatMessage } = useIntl();

  const originalQuery = useOriginalSearchQuery();
  const { pushQueryChange } = usePushQueryChange({ trailingSlash: true });
  const { inspiringSearchVersion } = useInspiringSearchSettings();

  const { isTransitioning } = useRouterTransition();

  const isV2 = inspiringSearchVersion === INSPIRING_SEARCH_V2;

  const canLoadMore = availableAdditionalSuggestions.length > 0;

  const loadMore = () => {
    const newPersistedData = {
      loaded: (
        persistedData?.loaded ??
        originalSuggestions.map(({ query, activeFilters }) => ({ query, activeFilters }))
      ).concat(availableAdditionalSuggestions.slice(0, LOAD_MORE_COUNT)),
      available: availableAdditionalSuggestions.slice(LOAD_MORE_COUNT),
    };

    pushQueryChange(encodeInspiringSearchData(newPersistedData), { scroll: false });
  };

  useEffect(() => {
    setOutcome(INSPIRING_SEARCH_TEST_ID, { SearchBuddyVisited: 1 });
  }, [setOutcome]);

  return (
    <>
      <Box display="flex" flexDirection="column" marginX={2} position="relative">
        <ShimmeringBlurOverlay show={isTransitioning} />
        <ColorSelectFailedNotification />

        {inspiringSearchVersion === INSPIRING_SEARCH_V2 && (
          <Typography variant="h3" sx={{ alignSelf: 'center', fontWeight: 600, marginTop: 1 }}>
            {formatMessage(messages.pageTitle)}
          </Typography>
        )}
        <Typography
          variant="body2"
          sx={{
            alignSelf: 'center',
            marginTop: inspiringSearchVersion === INSPIRING_SEARCH_V2 ? 1.5 : 2.5,
          }}
        >
          {formatMessage(messages.header)}
        </Typography>
        <Typography variant="body2" sx={{ alignSelf: 'center', marginBottom: 2 }}>
          „{originalQuery}“
        </Typography>

        {data.suggestions
          .filter((suggestion) => suggestion.items.length)
          .map((suggestion) => (
            <InspiringSearchSuggestion key={suggestion.query} maskedSuggestion={suggestion} />
          ))}

        {isV2 && canLoadMore && (
          <Button size="large" sx={{ alignSelf: 'center', my: 3 }} onClick={loadMore}>
            {formatMessage(messages.loadMore)}
          </Button>
        )}
      </Box>
      <ErrorBoundary>
        <SearchKimRating
          maskedData={data}
          searchPageType="SERP"
          templateParams={{ x17: referrer ?? 'x' }}
        />
      </ErrorBoundary>
    </>
  );
};
